import React, { useState } from 'react';
import { ActivityIndicator, Image, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToastStore } from '@/store/toastStore';
import {
  SERVICE_PHOTO_MAX,
  useAddServicePhoto,
  useDeleteServicePhoto,
  useServicePhotos,
} from '@/hooks/use-service-photos';
import { getApiErrorMessage } from '@/utils/api-error';

/**
 * Fotos do serviço (máx. 3): o prestador adiciona/remove aqui; viram o
 * carrossel de capa na tela pública do serviço vista pelo cliente.
 */
export function ServicePhotosManager({ serviceId }: { serviceId?: string }) {
  const { addToast } = useToastStore();
  const { data: photos, isLoading } = useServicePhotos(serviceId);
  const addPhoto = useAddServicePhoto(serviceId);
  const deletePhoto = useDeleteServicePhoto(serviceId);
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);

  const count = photos?.length ?? 0;
  const canAdd = count < SERVICE_PHOTO_MAX;

  const pickAndUpload = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permita o acesso às fotos para adicionar.', 'error');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.8,
    });
    if (result.canceled || !result.assets[0]) return;

    try {
      await addPhoto.mutateAsync({ imageUri: result.assets[0].uri });
      addToast('Foto adicionada.', 'success');
    } catch (e: any) {
      addToast(getApiErrorMessage(e, 'Erro ao enviar foto.'), 'error');
    }
  };

  const confirmRemove = async () => {
    if (!photoToDelete) return;
    try {
      await deletePhoto.mutateAsync(photoToDelete);
      addToast('Foto removida.', 'success');
    } catch (e: any) {
      addToast(getApiErrorMessage(e, 'Erro ao remover foto.'), 'error');
    } finally {
      setPhotoToDelete(null);
    }
  };

  return (
    <View className="w-full gap-3 px-5 pt-5">
      <View className="flex-row items-center justify-between">
        <ThemedText className="text-base font-semibold text-foreground">
          Fotos do serviço
        </ThemedText>
        <ThemedText className="text-xs text-neutral-500">
          {count}/{SERVICE_PHOTO_MAX}
        </ThemedText>
      </View>
      <ThemedText className="text-xs text-neutral-500">
        Aparecem no topo da página do serviço para o cliente.
      </ThemedText>

      {isLoading ? (
        <View className="items-center py-6">
          <ActivityIndicator className="text-primary" />
        </View>
      ) : (
        <View className="flex-row flex-wrap gap-3">
          {photos?.map((photo) => (
            <View
              key={photo.id}
              className="overflow-hidden rounded-2xl bg-neutral-100"
              style={{ width: 96, height: 96, borderCurve: 'continuous' }}
            >
              <Image
                source={{ uri: photo.url }}
                accessibilityLabel="Foto do serviço"
                style={{ width: '100%', height: '100%' }}
                resizeMode="cover"
              />
              <PressableScale
                onPress={() => setPhotoToDelete(photo.id)}
                disabled={deletePhoto.isPending}
                className="absolute right-1 top-1 h-6 w-6 items-center justify-center rounded-full bg-black/55"
                accessibilityLabel="Remover foto"
              >
                <Ionicons name="close" size={14} color="#fff" />
              </PressableScale>
            </View>
          ))}

          {canAdd && (
            <PressableScale
              haptic
              onPress={pickAndUpload}
              disabled={addPhoto.isPending}
              className="items-center justify-center rounded-2xl border border-dashed border-neutral-300 bg-neutral-50"
              style={{ width: 96, height: 96, borderCurve: 'continuous' }}
            >
              {addPhoto.isPending ? (
                <ActivityIndicator className="text-primary" />
              ) : (
                <>
                  <Ionicons className="text-neutral-500" name="add" size={26} />
                  <ThemedText className="mt-0.5 text-[11px] text-neutral-500">
                    Adicionar
                  </ThemedText>
                </>
              )}
            </PressableScale>
          )}
        </View>
      )}

      <Dialog
        open={!!photoToDelete}
        onOpenChange={(open) => !open && setPhotoToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover foto</DialogTitle>
            <DialogDescription>
              Tem certeza? A foto sai da página do serviço. Esta ação não pode
              ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex-row gap-3">
            <Button
              title="Cancelar"
              variant="outline"
              onPress={() => setPhotoToDelete(null)}
              disabled={deletePhoto.isPending}
              className="flex-1"
            />
            <Button
              title="Remover"
              variant="destructive"
              onPress={confirmRemove}
              disabled={deletePhoto.isPending}
              className="flex-1"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </View>
  );
}
